'use client';

import {OTPInput, OTPInputContext} from 'input-otp';
import {Dot} from 'lucide-react';
import {ComponentPropsWithoutRef, ElementRef, forwardRef, useContext} from 'react';

import {cn} from '../../libs';

export const InputOTP = forwardRef<
  ElementRef<typeof OTPInput>,
  ComponentPropsWithoutRef<typeof OTPInput>
>(({className, containerClassName, ...props}, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn('has-disabled:opacity-50 flex items-center gap-2', containerClassName)}
    className={cn('disabled:cursor-not-allowed', className)}
    {...props}
  />
));

InputOTP.displayName = 'InputOTP';

export const InputOTPGroup = forwardRef<ElementRef<'div'>, ComponentPropsWithoutRef<'div'>>(
  ({className, ...props}, ref) => (
    <div ref={ref} className={cn('flex items-center', className)} {...props} />
  ),
);

InputOTPGroup.displayName = 'InputOTPGroup';

export const InputOTPSlot = forwardRef<
  ElementRef<'div'>,
  ComponentPropsWithoutRef<'div'> & {index: number}
>(({index, className, ...props}, ref) => {
  const inputOTPContext = useContext(OTPInputContext);
  const {char, hasFakeCaret, isActive} = inputOTPContext.slots[index];

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex size-10 items-center justify-center rounded-md border border-input-border font-medium text-[26px] transition-all',
        isActive && 'border-input-border-focus',
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-text-primary duration-1000" />
        </div>
      )}
    </div>
  );
});

InputOTPSlot.displayName = 'InputOTPSlot';

export const InputOTPSeparator = forwardRef<ElementRef<'div'>, ComponentPropsWithoutRef<'div'>>(
  ({...props}, ref) => (
    <div ref={ref} {...props}>
      <Dot />
    </div>
  ),
);

InputOTPSeparator.displayName = 'InputOTPSeparator';
